<template>
	<FullLayout>
		<template #topbar>
			<div class="topbar-content">
				<span class="fw-semibold">Notes</span>
			</div>
		</template>

		<div class="notes-map">
			<mgl-map
				:map-style="mapStyleURL(ALIDADE_SMOOTH)"
				:zoom="5"
				@map:load="onMapLoad"
			>
				<!-- The open note's recorded breadcrumb track, drawn under its
				     own point markers. -->
				<mgl-geo-json-source source-id="note-track" :data="trackGeoJSON">
					<mgl-line-layer
						layer-id="note-track-line"
						:paint="{
							'line-color': '#0d6efd',
							'line-width': 3,
							'line-opacity': 0.85,
						}"
						:layout="{ 'line-cap': 'round', 'line-join': 'round' }"
					/>
					<mgl-circle-layer
						layer-id="note-track-points"
						:paint="{
							'circle-radius': 4,
							'circle-color': '#0d6efd',
							'circle-stroke-color': '#fff',
							'circle-stroke-width': 2,
						}"
					/>
				</mgl-geo-json-source>

				<!-- Photo locations, drawn above the breadcrumbs. -->
				<mgl-geo-json-source source-id="note-photos" :data="photoGeoJSON">
					<mgl-circle-layer
						layer-id="note-photo-points"
						:paint="{
							'circle-radius': 6,
							'circle-color': '#fd7e14',
							'circle-stroke-color': '#fff',
							'circle-stroke-width': 2,
						}"
					/>
				</mgl-geo-json-source>
			</mgl-map>
		</div>

		<ToolRail
			side="left"
			v-model="leftTab"
			:tabs="[{ key: 'notes', icon: 'bi bi-list-ul', title: 'Notes' }]"
			topOffset="60px"
		>
			<template #notes>
				<ReviewTranscriptColumnList
					:transcripts="queue"
					:loading="loadingQueue"
					:selected-id="detail?.id"
					:has-more="hasMore"
					:error="queueError"
					@selection-change="handleSelectionChange"
					@filters-change="handleFiltersChange"
					@load-more="loadNextPage"
				/>
			</template>
		</ToolRail>

		<ToolRail
			side="right"
			v-model="rightTab"
			:tabs="[{ key: 'note', icon: 'bi bi-file-earmark-text', title: 'Note' }]"
			topOffset="60px"
			bottomOffset="60px"
		>
			<template #note>
				<ReviewTranscriptColumnDetail
					:transcript="detail"
					:loading="loadingDetail"
					:error="detailError"
					@note-changed="handleNoteChanged"
					@focus-location="focusLocation"
				/>
			</template>
		</ToolRail>
	</FullLayout>
</template>

<script setup lang="ts">
import "maplibre-gl/dist/maplibre-gl.css";

import {
	computed,
	onBeforeUnmount,
	onMounted,
	ref,
	shallowRef,
	watch,
} from "vue";
import type { LngLatBounds, Map } from "maplibre-gl";
import {
	MglCircleLayer,
	MglGeoJsonSource,
	MglLineLayer,
	MglMap,
	type MglEvent,
} from "vue-maplibre-gl";
import type { GeoJSON } from "geojson";

import FullLayout from "@/components/layout/Full.vue";
import ToolRail from "@/components/panel/ToolRail.vue";
import ReviewTranscriptColumnDetail from "@/components/ReviewTranscriptColumnDetail.vue";
import ReviewTranscriptColumnList from "@/components/ReviewTranscriptColumnList.vue";
import { apiClient } from "@/client";
import { SSEManager, type SSEMessageResource } from "@/SSEManager";
import { ALIDADE_SMOOTH } from "@/map/constants";
import { boundsDefault, boundsFromGeoJSON, mapStyleURL } from "@/map/util";
import type {
	MapReviewQueryBody,
	MapReviewQueryEnvelope,
	TranscriptLocationPoint,
	TranscriptNote,
} from "@/type/api";

const QUEUE_LIMIT = 50;

// ── Floating panels ──

const leftTab = ref<string | null>("notes");
const rightTab = ref<string | null>("note");

// ── Queue (QUERY /api/transcript — newest first, server filters) ──

const queue = ref<TranscriptNote[]>([]);
const loadingQueue = ref(false);
const queueError = ref<string | null>(null);
const hasMore = ref(false);
/** The queue's narrowing: the generic body the filter panel reports, without
 * paging — the page in hand is what states the offset (see loadQueue). */
const queueBody = ref<MapReviewQueryBody>({});
let queueRequestSeq = 0;

const apiErrorMessage = (error: unknown, fallback: string): string => {
	if (error instanceof Error && error.message) return error.message;
	return fallback;
};

/**
 * Fetch a queue page through the generic QUERY endpoint
 * (doc/in-progress/query.md §11 Phase 4). `reset` replaces the loaded rows (new
 * filter set / after a note mutation); otherwise the page resumes where the rows
 * in hand stop.
 *
 * Paging: the offset is the loaded row count, not the envelope's `cursor`. The
 * queue prepends a note the event stream announces, and the rows in hand are then
 * still exactly the collection's first `queue.length`, so the page read from that
 * offset is the first row the reviewer has not seen. The envelope's cursor counts
 * from the head of a collection that prepend has just moved.
 *
 * The legacy read paged by a keyset cursor on `(created, id)`, which this read
 * replaces with offset scanning: a note DELETED between two pages can slide a row
 * past the boundary. The queue is append-only from the client's side and every
 * mutation reloads page one, so the slide is limited to a concurrent deletion.
 *
 * Newest-first is the resource's own default order, so no order is stated.
 */
const loadQueue = async (reset: boolean) => {
	const seq = ++queueRequestSeq;
	const offset = reset ? 0 : queue.value.length;
	loadingQueue.value = true;
	queueError.value = null;
	try {
		const page = await apiClient.JSONQuery<
			MapReviewQueryEnvelope<TranscriptNote>
		>("/api/transcript", {
			...queueBody.value,
			limit: QUEUE_LIMIT,
			offset,
		} satisfies MapReviewQueryBody);
		if (seq !== queueRequestSeq) return; // superseded by a newer request
		queue.value = reset ? page.items : [...queue.value, ...page.items];
		hasMore.value = page.metadata.cursor !== null;
	} catch (error) {
		if (seq !== queueRequestSeq) return;
		queueError.value = apiErrorMessage(error, "Could not load the queue");
	} finally {
		if (seq === queueRequestSeq) loadingQueue.value = false;
	}
};

const loadNextPage = () => {
	if (loadingQueue.value) return;
	void loadQueue(false);
};

// ── Detail (GET /api/transcript/{id}) ──

const detail = ref<TranscriptNote | undefined>(undefined);
const loadingDetail = ref(false);
const detailError = ref<string | null>(null);
let detailRequestSeq = 0;

/** Load the full note; keep the current one visible while refreshing. */
const loadDetail = async (id: string, keepCurrent: boolean) => {
	const seq = ++detailRequestSeq;
	if (!keepCurrent) {
		detail.value = undefined;
		detailError.value = null;
	}
	loadingDetail.value = true;
	try {
		const note = await apiClient.JSONGet<TranscriptNote>(
			`/api/transcript/${encodeURIComponent(id)}`,
		);
		if (seq !== detailRequestSeq) return;
		detail.value = note;
		detailError.value = null;
	} catch (error) {
		if (seq !== detailRequestSeq) return;
		detailError.value = apiErrorMessage(
			error,
			`Could not load transcript #${id}`,
		);
	} finally {
		if (seq === detailRequestSeq) loadingDetail.value = false;
	}
};

const handleSelectionChange = (transcript: TranscriptNote | undefined) => {
	if (transcript === undefined) {
		detail.value = undefined;
		return;
	}
	// Queue rows carry empty heavy fields — always fetch the full note.
	if (detail.value?.id === transcript.id) return;
	void loadDetail(transcript.id, false);
};

const handleFiltersChange = (body: MapReviewQueryBody) => {
	queueBody.value = body;
	void loadQueue(true);
};

/** A review mutation landed (edit/status/extraction/comment/clarify). */
const handleNoteChanged = () => {
	if (detail.value) void loadDetail(detail.value.id, true);
	void loadQueue(true);
};

// ── Live queue (SSE) ──

// The mobile client uploads notes through the same endpoints the review queue
// reads, and note creation is published on the event stream as
// `sync:note:audio` with a `/api/note/<id>` URI. Only audio notes belong here:
// the queue is the transcript review module (`/api/transcript`), and
// `note_image` ids are an unrelated sequence, so an image event's id must not
// be fetched as a transcript.
const noteIdFromURI = (uri: string): string | undefined => {
	const match = /\/api\/note\/([^/]+)$/.exec(uri);
	return match ? decodeURIComponent(match[1]) : undefined;
};

/** Fetch an arrived note and put it at the head of the queue (newest first). */
const addNoteToQueue = async (id: string) => {
	if (queue.value.some((note) => note.id === id)) return;
	try {
		const note = await apiClient.JSONGet<TranscriptNote>(
			`/api/transcript/${encodeURIComponent(id)}`,
		);
		if (queue.value.some((existing) => existing.id === note.id)) return;
		queue.value = [note, ...queue.value];
	} catch {
		// The event can outrun the note's transaction, or name a note this
		// reviewer's org cannot read; either way there is nothing to add.
	}
};

const handleSSEEvent = (msg: SSEMessageResource) => {
	if (msg.type !== "created" || msg.resource !== "sync:note:audio") return;
	const id = noteIdFromURI(msg.uri);
	if (id === undefined) return;
	void addNoteToQueue(id);
};

let sseSubscription: string | null = null;
let sseReconnectSubscription: string | null = null;
// `onMounted` is async: a navigation away during the initial queue load would
// otherwise subscribe after teardown and leak the handlers.
let unmounted = false;

// ── Map (the open note's spatial evidence, derived from the detail read) ──

const emptyFeatures: GeoJSON.FeatureCollection = {
	type: "FeatureCollection",
	features: [],
};

const pointFeature = (
	longitude: number,
	latitude: number,
): GeoJSON.Feature => ({
	type: "Feature",
	properties: {},
	geometry: { type: "Point", coordinates: [longitude, latitude] },
});

/**
 * The open note's recorded breadcrumb track: the location stream as a line,
 * plus one point per breadcrumb. Empty until a note is open — queue rows
 * carry no `locationStream`.
 */
const trackGeoJSON = computed<GeoJSON.FeatureCollection>(() => {
	const breadcrumbs = detail.value?.locationStream ?? [];
	if (breadcrumbs.length === 0) return emptyFeatures;
	const features: GeoJSON.Feature[] = breadcrumbs.map((point) =>
		pointFeature(point.longitude, point.latitude),
	);
	if (breadcrumbs.length > 1) {
		features.push({
			type: "Feature",
			properties: {},
			geometry: {
				type: "LineString",
				coordinates: breadcrumbs.map((point) => [
					point.longitude,
					point.latitude,
				]),
			},
		});
	}
	return { type: "FeatureCollection", features };
});

/** Where the open note's photos were taken, one point per photo. */
const photoGeoJSON = computed<GeoJSON.FeatureCollection>(() => {
	const photos = detail.value?.photos ?? [];
	return {
		type: "FeatureCollection",
		features: photos.map((photo) =>
			pointFeature(photo.location.longitude, photo.location.latitude),
		),
	};
});

const mapRef = shallowRef<Map | null>(null);

/** Bounds of everything the open note plotted, or null when it plotted none. */
const noteBounds = (): LngLatBounds | null => {
	const track = boundsFromGeoJSON(trackGeoJSON.value, 20);
	const photos = boundsFromGeoJSON(photoGeoJSON.value, 20);
	if (!track) return photos;
	if (photos) track.extend(photos);
	return track;
};

/** Frame the open note's track; the continental default when there is none. */
const fitMap = () => {
	const map = mapRef.value;
	if (!map) return;
	const bounds = noteBounds();
	if (bounds) {
		map.fitBounds(bounds, { padding: 60, maxZoom: 17, duration: 800 });
	} else {
		map.fitBounds(boundsDefault(), { padding: 60, duration: 800 });
	}
};

const onMapLoad = (event: MglEvent<"load">) => {
	mapRef.value = event.map;
	fitMap();
};

/**
 * A ● marker on the note's timeline was clicked — center the map on that
 * recorded location (the detail pane no longer carries its own map inset).
 */
const focusLocation = (point: TranscriptLocationPoint) => {
	mapRef.value?.flyTo({
		center: [point.longitude, point.latitude],
		zoom: 17,
		duration: 800,
	});
};

// Opening another note re-frames the map on its own track (a review mutation
// re-reads the same note and leaves the view alone).
watch(() => detail.value?.id, fitMap);

onMounted(async () => {
	await loadQueue(true);
	if (unmounted) return;
	// Newest first: open the top transcript on first entry.
	const first = queue.value[0];
	if (first) void loadDetail(first.id, false);

	// Live queue. Subscribe after the initial read so an arriving note cannot
	// race the first page's replacement of `queue`; a reconnect resyncs the
	// page (and any note events missed while the stream was down).
	sseSubscription = SSEManager.subscribe(handleSSEEvent);
	sseReconnectSubscription = SSEManager.subscribeReconnect(() => {
		void loadQueue(true);
	});
});

onBeforeUnmount(() => {
	unmounted = true;
	if (sseSubscription !== null) SSEManager.unsubscribe(sseSubscription);
	if (sseReconnectSubscription !== null) {
		SSEManager.unsubscribe(sseReconnectSubscription);
	}
});
</script>

<style scoped>
.topbar-content {
	display: flex;
	align-items: center;
	height: 48px;
	padding: 0 16px;
	background: #fff;
	border-bottom: 1px solid #dee2e6;
	box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}

.notes-map {
	position: absolute;
	inset: 0;
}

/* Ensure the map fills the full-viewport wrapper. */
.notes-map :deep(.maplibregl-map) {
	width: 100%;
	height: 100%;
}

.notes-map :deep(.maplibregl-canvas) {
	width: 100%;
	height: 100%;
}
</style>
